/**
 * Watch list lives in localStorage. No backend, no sync. The whole point is
 * that this is a personal pinning layer over RaceDay — losing it is not a
 * disaster, and avoiding accounts keeps the app trivially deployable.
 */

const KEY = "raceday-permalink:watchlist:v1";

export interface WatchList {
  /** Case-insensitive substrings to match against the result `name` field. */
  riders: string[];
  /** Event IDs the user has saved for quick access. */
  events: string[];
}

const empty: WatchList = { riders: [], events: [] };

function read(): WatchList {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...empty };
    const parsed = JSON.parse(raw) as Partial<WatchList>;
    return {
      riders: Array.isArray(parsed.riders) ? parsed.riders : [],
      events: Array.isArray(parsed.events) ? parsed.events : [],
    };
  } catch {
    return { ...empty };
  }
}

function write(list: WatchList): void {
  localStorage.setItem(KEY, JSON.stringify(list));
  window.dispatchEvent(new CustomEvent("watchlist-changed"));
}

export function getWatchList(): WatchList {
  return read();
}

export function addRider(name: string): WatchList {
  const list = read();
  const normalized = name.trim();
  if (normalized && !list.riders.some((r) => r.toLowerCase() === normalized.toLowerCase())) {
    list.riders.push(normalized);
    write(list);
  }
  return list;
}

export function removeRider(name: string): WatchList {
  const list = read();
  list.riders = list.riders.filter(
    (r) => r.toLowerCase() !== name.toLowerCase(),
  );
  write(list);
  return list;
}

export function addEvent(eventId: string): WatchList {
  const list = read();
  if (!list.events.includes(eventId)) {
    list.events.push(eventId);
    write(list);
  }
  return list;
}

export function removeEvent(eventId: string): WatchList {
  const list = read();
  list.events = list.events.filter((e) => e !== eventId);
  write(list);
  return list;
}

export function lastNameOf(name: string): string {
  const trimmed = name.trim();
  const idx = trimmed.indexOf(", ");
  if (idx === -1) return trimmed;
  return trimmed.slice(0, idx).trim();
}

export function isRowWatched(rowName: string, riders: string[]): boolean {
  const lower = rowName.toLowerCase();
  const idx = lower.indexOf(", ");
  if (idx === -1) {
    return riders.some((r) => lower.includes(r.trim().toLowerCase()));
  }
  const last = lower.slice(0, idx);
  const first = lower.slice(idx + 2);
  return riders.some((r) => {
    const needle = r.trim().toLowerCase();
    return last.includes(needle) || first.includes(needle);
  });
}
